'use client';

import { Study } from '@/lib/study';
import { T } from '@/lib/tokens';
import { Nav, StepHead, StepWrap } from '../StepShell';

type Props = {
  data: Study;
  setData: (d: Study) => void;
  onBack: () => void;
  onNext: () => void;
};

export function Step7FollowUps({ data, setData, onBack, onNext }: Props) {
  const max = data.maxFollowUps ?? 2;
  const setMax = (v: number) => setData({ ...data, maxFollowUps: v });

  return (
    <StepWrap>
      <StepHead
        label="Step 8 — Follow-up Questions"
        title="How many follow-ups should the AI ask?"
        subtitle="After each main question, the AI may ask follow-up probes to go deeper. Set the maximum number of follow-ups per question."
      />

      <div
        style={{
          background: T.white,
          border: `1.5px solid ${T.creamBorder}`,
          borderRadius: 14,
          padding: '28px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <button onClick={() => setMax(Math.max(0, max - 1))} style={bigBtn} type="button">
            −
          </button>
          <div style={{ textAlign: 'center', minWidth: 60 }}>
            <div className="serif" style={{ fontSize: 48, color: T.terra, lineHeight: 1 }}>
              {max}
            </div>
            <div
              style={{
                fontSize: 12,
                color: T.espressoLight,
                marginTop: 4,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {max === 1 ? 'follow-up' : 'follow-ups'}
            </div>
          </div>
          <button onClick={() => setMax(Math.min(5, max + 1))} style={bigBtn} type="button">
            +
          </button>
          <div style={{ fontSize: 13, color: T.espressoLight }}>max 5</div>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              onClick={() => setMax(n)}
              style={{
                flex: 1,
                height: 6,
                borderRadius: 99,
                cursor: 'pointer',
                background: n <= max ? T.terra : T.creamMid,
                transition: 'background 0.2s',
              }}
            />
          ))}
        </div>

        <div
          style={{
            fontSize: 14,
            color: T.espressoMid,
            lineHeight: 1.65,
            background: T.terraLight,
            borderRadius: 8,
            padding: '12px 14px',
          }}
        >
          {max === 0 && <span>The AI will ask each main question exactly once, with no follow-ups.</span>}
          {max === 1 && (
            <span>
              After each main question, the AI may ask <strong style={{ color: T.espresso }}>1 follow-up</strong> if
              the response feels incomplete or worth exploring further.
            </span>
          )}
          {max === 2 && (
            <span>
              After each main question, the AI may ask up to{' '}
              <strong style={{ color: T.espresso }}>2 follow-ups</strong> — a good balance for a 20-minute interview.{' '}
              <em>(Recommended)</em>
            </span>
          )}
          {max >= 3 && (
            <span>
              After each main question, the AI may ask up to{' '}
              <strong style={{ color: T.espresso }}>{max} follow-ups</strong>. This allows deeper exploration but may
              extend the session length.
            </span>
          )}
        </div>
      </div>

      <Nav onBack={onBack} onNext={onNext} />
    </StepWrap>
  );
}

const bigBtn: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 10,
  border: `1.5px solid ${T.creamBorder}`,
  background: T.cream,
  cursor: 'pointer',
  fontSize: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: T.espresso,
  fontFamily: 'inherit',
};
